import ChalkySticks from '@chalkysticks/sdk';
import Store from '@/Store';
import { App } from '@capacitor/app';

/**
 * Provides Capacitor-based geolocation methods.
 *
 * @author Matt
 * @package Service
 * @project ChalkySticks
 */
export class Location {
	/**
	 * @type number | null
	 */
	private geolocationWatchId: number | null = null;

	/**
	 * @type boolean
	 */
	private hasSetup: boolean = false;

	/**
	 * Whether tracking has been externally suspended. When true,
	 * the app-state-change listener will not restart tracking.
	 *
	 * @type boolean
	 */
	private isSuspended: boolean = false;

	/**
	 * @type boolean
	 */
	private isTracking: boolean = false;

	/**
	 * Checks location permission status.
	 *
	 * @return Promise<void>
	 */
	public async start(): Promise<void> {
		if (this.hasSetup) {
			console.warn('Location service already set up, skipping initialization.');
			return;
		}

		// Wait a tick
		await ChalkySticks.Utility.sleep(100);

		// Start immediately
		this.startTracking();

		// Handle app state changes to pause/resume tracking
		App.addListener('appStateChange', (state) => {
			if (this.isSuspended) {
				return;
			}

			if (state.isActive) {
				this.startTracking();
			} else {
				this.stop();
			}
		});

		// Mark setup so we don't do this again
		this.hasSetup = true;
	}

	/**
	 * Resumes geolocation tracking after a prior suspend() call.
	 * No-op if tracking was not suspended.
	 *
	 * @return void
	 */
	public resume(): void {
		if (!this.isSuspended) {
			return;
		}

		this.isSuspended = false;
		this.startTracking();

		ChalkySticks.Utility.Log.debug('location', '⏯ Resuming Location Services');
	}

	/**
	 * Checks location permission status.
	 *
	 * @return Promise<void>
	 */
	public async startTracking(): Promise<void> {
		if (this.isTracking) {
			return;
		}

		ChalkySticks.Utility.Geolocation.watchLocation(this.Handle_OnLocationSuccess);
		ChalkySticks.Utility.Log.debug('location', '🟩 Starting Location Services');

		this.isTracking = true;
	}

	/**
	 * Temporarily suspends geolocation tracking and stops the GPS sensor.
	 * While suspended, the app-state-change listener will not restart tracking.
	 * Call resume() to restore tracking.
	 *
	 * @return void
	 */
	public suspend(): void {
		if (!this.isTracking && !this.hasSetup) {
			return;
		}

		this.isSuspended = true;
		this.stop();

		ChalkySticks.Utility.Log.debug('location', '⏸ Suspending Location Services');
	}

	/**
	 * Stops geolocation tracking
	 *
	 * @return void
	 */
	private stop(): void {
		ChalkySticks.Utility.Geolocation.clearWatch();
		ChalkySticks.Utility.Log.debug('location', '🟥 Stopping Location Services');

		this.isTracking = false;
	}

	/**
	 * @param Event e
	 * @return Promise<void>
	 */
	protected async Handle_OnLocationSuccess(response: any): Promise<void> {
		ChalkySticks.Utility.Log.debug('location', '✜ Location Success', response);
		Store.dispatch('location/position', response.position);
	}
}

export default new Location();
